import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

import { type FrameRate, frameRates } from '@cartwhl/common/types';

interface SourceFrameToPlaybackFrameInput {
  bvh: BVH | null | undefined;
  frame: number;
  maxPlaybackFrame?: number;
  playbackFrameRate: number;
}

interface PlaybackFrameToSourceFrameInput {
  bvh: BVH | null | undefined;
  frame: number;
  playbackFrameRate: number;
}

type KeyframeTimes = ArrayLike<number>;

export function getSourceBvhForTimeline(
  mergedBvh: BVH | null,
  bvhs: readonly BVH[],
  timelineIndex: number | null | undefined,
) {
  if (timelineIndex !== null && timelineIndex !== undefined) {
    return bvhs[timelineIndex] ?? mergedBvh;
  }

  return mergedBvh;
}

export function normalizePlaybackFrame(frame: number, maxFrame?: number) {
  return clampRoundedFrame(frame, maxFrame);
}

export function resolveBvhFrameRate(
  bvh: BVH | null | undefined,
): FrameRate | null {
  const keyframeTimes = getKeyframeTimes(bvh);
  if (!keyframeTimes || keyframeTimes.length < 2) {
    return null;
  }

  for (let frame = 1; frame < keyframeTimes.length; frame += 1) {
    const previousTime = getFiniteTimeAtFrame(keyframeTimes, frame - 1);
    const currentTime = getFiniteTimeAtFrame(keyframeTimes, frame);
    if (previousTime === null || currentTime === null) {
      continue;
    }

    const frameTime = currentTime - previousTime;
    if (!Number.isFinite(frameTime) || frameTime <= 0) {
      continue;
    }

    const frameRate = Math.round(1 / frameTime);
    return frameRates.includes(frameRate as FrameRate)
      ? (frameRate as FrameRate)
      : null;
  }

  return null;
}

export function formatInterpolatedFrameSnapNotice(
  requestedPlaybackFrame: number,
  resolvedPlaybackFrame: number,
) {
  return `Frame ${requestedPlaybackFrame + 1} is an interpolated frame and cannot have a key pose on it. Adding key pose to ${resolvedPlaybackFrame + 1} instead.`;
}

/** Some playback frames cannot host a key pose when source BVH fps differs from playback fps. */
export function resolveKeyPosePlaybackFrame({
  bvh,
  frame,
  maxPlaybackFrame,
  playbackFrameRate,
}: SourceFrameToPlaybackFrameInput) {
  const requestedPlaybackFrame = clampRoundedFrame(frame, maxPlaybackFrame);
  const keyframeTimes = getKeyframeTimes(bvh);
  const frameRate = getValidPlaybackFrameRate(playbackFrameRate);
  if (!keyframeTimes || !frameRate) {
    return requestedPlaybackFrame;
  }

  if (
    isPlaybackFrameReachable({
      bvh,
      maxPlaybackFrame,
      playbackFrame: requestedPlaybackFrame,
      playbackFrameRate,
    })
  ) {
    return requestedPlaybackFrame;
  }

  const sourceFrame = playbackFrameToSourceFrame({
    bvh,
    frame: requestedPlaybackFrame,
    playbackFrameRate,
  });

  return sourceFrameToPlaybackFrame({
    bvh,
    frame: sourceFrame,
    maxPlaybackFrame,
    playbackFrameRate,
  });
}

export function findSourceFrameForPlaybackFrame({
  bvh,
  frame,
  maxPlaybackFrame,
  playbackFrameRate,
}: SourceFrameToPlaybackFrameInput) {
  const targetPlaybackFrame = clampRoundedFrame(frame, maxPlaybackFrame);
  const keyframeTimes = getKeyframeTimes(bvh);
  if (!keyframeTimes) {
    return targetPlaybackFrame;
  }

  for (
    let sourceFrame = 0;
    sourceFrame < keyframeTimes.length;
    sourceFrame += 1
  ) {
    const playbackFrame = sourceFrameToPlaybackFrame({
      bvh,
      frame: sourceFrame,
      maxPlaybackFrame,
      playbackFrameRate,
    });
    if (playbackFrame === targetPlaybackFrame) {
      return sourceFrame;
    }
  }

  return null;
}

export function sourceFrameToPlaybackFrame({
  bvh,
  frame,
  maxPlaybackFrame,
  playbackFrameRate,
}: SourceFrameToPlaybackFrameInput) {
  const fallbackFrame = clampRoundedFrame(frame, maxPlaybackFrame);
  const keyframeTimes = getKeyframeTimes(bvh);
  const frameRate = getValidPlaybackFrameRate(playbackFrameRate);
  if (!keyframeTimes || !frameRate) {
    return fallbackFrame;
  }

  const sourceFrame = clampRoundedFrame(frame, keyframeTimes.length - 1);
  const sourceTime = getFiniteTimeAtFrame(keyframeTimes, sourceFrame);
  if (sourceTime === null) {
    return fallbackFrame;
  }

  return clampRoundedFrame(sourceTime * frameRate, maxPlaybackFrame);
}

export function playbackFrameToSourceFrame({
  bvh,
  frame,
  playbackFrameRate,
}: PlaybackFrameToSourceFrameInput) {
  const fallbackFrame = clampRoundedFrame(frame);
  const keyframeTimes = getKeyframeTimes(bvh);
  const frameRate = getValidPlaybackFrameRate(playbackFrameRate);
  if (!keyframeTimes || !frameRate) {
    return fallbackFrame;
  }

  const targetTime = fallbackFrame / frameRate;

  return findNearestKeyframeIndex(keyframeTimes, targetTime);
}

export function playbackFrameToKeyPoseSourceFrame({
  bvh,
  frame,
  playbackFrameRate,
  maxPlaybackFrame,
}: PlaybackFrameToSourceFrameInput & { maxPlaybackFrame?: number }) {
  const targetPlaybackFrame = resolveKeyPosePlaybackFrame({
    bvh,
    frame,
    maxPlaybackFrame,
    playbackFrameRate,
  });
  const exactSourceFrame = findSourceFrameForPlaybackFrame({
    bvh,
    frame: targetPlaybackFrame,
    maxPlaybackFrame,
    playbackFrameRate,
  });
  if (exactSourceFrame !== null) {
    return exactSourceFrame;
  }

  const fallbackFrame = playbackFrameToSourceFrame({
    bvh,
    frame: targetPlaybackFrame,
    playbackFrameRate,
  });
  const keyframeTimes = getKeyframeTimes(bvh);
  const frameRate = getValidPlaybackFrameRate(playbackFrameRate);
  if (!keyframeTimes || !frameRate) {
    return fallbackFrame;
  }

  const targetTime = targetPlaybackFrame / frameRate;
  const maxSourceFrame = keyframeTimes.length - 1;
  const candidates = getNearbyFrameCandidates(fallbackFrame, maxSourceFrame);

  return candidates.reduce((bestFrame, candidateFrame) => {
    return compareSourceFrameCandidates({
      bestFrame,
      candidateFrame,
      bvh,
      keyframeTimes,
      playbackFrameRate,
      targetPlaybackFrame,
      targetTime,
    }) <= 0
      ? bestFrame
      : candidateFrame;
  }, fallbackFrame);
}

function isPlaybackFrameReachable({
  bvh,
  maxPlaybackFrame,
  playbackFrame,
  playbackFrameRate,
}: {
  bvh: BVH | null | undefined;
  maxPlaybackFrame?: number;
  playbackFrame: number;
  playbackFrameRate: number;
}) {
  return (
    findSourceFrameForPlaybackFrame({
      bvh,
      frame: playbackFrame,
      maxPlaybackFrame,
      playbackFrameRate,
    }) !== null
  );
}

function getKeyframeTimes(bvh: BVH | null | undefined) {
  const times = bvh?.clip.tracks[0]?.times;

  return times && times.length > 0 ? (times as KeyframeTimes) : null;
}

function getValidPlaybackFrameRate(playbackFrameRate: number) {
  return Number.isFinite(playbackFrameRate) && playbackFrameRate > 0
    ? playbackFrameRate
    : null;
}

function clampRoundedFrame(frame: number, maxFrame?: number) {
  const roundedFrame = Number.isFinite(frame) ? Math.round(frame) : 0;
  const normalizedFrame = Math.max(roundedFrame, 0);
  if (maxFrame === undefined) {
    return normalizedFrame;
  }

  return Math.min(normalizedFrame, Math.max(Math.round(maxFrame), 0));
}

function findNearestKeyframeIndex(
  keyframeTimes: KeyframeTimes,
  targetTime: number,
) {
  if (keyframeTimes.length <= 1) {
    return 0;
  }

  let lower = 0;
  let upper = keyframeTimes.length - 1;
  while (lower < upper) {
    const midpoint = Math.floor((lower + upper) / 2);
    const midpointTime = getFiniteTimeAtFrame(keyframeTimes, midpoint);
    if (midpointTime !== null && midpointTime < targetTime) {
      lower = midpoint + 1;
    } else {
      upper = midpoint;
    }
  }

  const nextFrame = lower;
  const previousFrame = Math.max(nextFrame - 1, 0);
  const nextTime = getFiniteTimeAtFrame(keyframeTimes, nextFrame);
  const previousTime = getFiniteTimeAtFrame(keyframeTimes, previousFrame);
  if (nextTime === null) {
    return previousFrame;
  }

  if (previousTime === null) {
    return nextFrame;
  }

  return Math.abs(previousTime - targetTime) <= Math.abs(nextTime - targetTime)
    ? previousFrame
    : nextFrame;
}

function getNearbyFrameCandidates(frame: number, maxFrame: number) {
  const normalizedFrame = clampRoundedFrame(frame, maxFrame);
  const candidates = new Set<number>();
  for (let offset = -2; offset <= 2; offset += 1) {
    candidates.add(clampRoundedFrame(normalizedFrame + offset, maxFrame));
  }

  return Array.from(candidates);
}

function compareSourceFrameCandidates({
  bestFrame,
  candidateFrame,
  bvh,
  keyframeTimes,
  playbackFrameRate,
  targetPlaybackFrame,
  targetTime,
}: {
  bestFrame: number;
  candidateFrame: number;
  bvh: BVH | null | undefined;
  keyframeTimes: KeyframeTimes;
  playbackFrameRate: number;
  targetPlaybackFrame: number;
  targetTime: number;
}) {
  const bestPlaybackFrame = sourceFrameToPlaybackFrame({
    bvh,
    frame: bestFrame,
    playbackFrameRate,
  });
  const candidatePlaybackFrame = sourceFrameToPlaybackFrame({
    bvh,
    frame: candidateFrame,
    playbackFrameRate,
  });
  const bestPlaybackDistance = Math.abs(
    bestPlaybackFrame - targetPlaybackFrame,
  );
  const candidatePlaybackDistance = Math.abs(
    candidatePlaybackFrame - targetPlaybackFrame,
  );

  if (bestPlaybackDistance !== candidatePlaybackDistance) {
    return bestPlaybackDistance - candidatePlaybackDistance;
  }

  const bestIsAfterTarget = bestPlaybackFrame > targetPlaybackFrame;
  const candidateIsAfterTarget = candidatePlaybackFrame > targetPlaybackFrame;
  if (bestIsAfterTarget !== candidateIsAfterTarget) {
    return bestIsAfterTarget ? 1 : -1;
  }

  const bestTime = getFiniteTimeAtFrame(keyframeTimes, bestFrame);
  const candidateTime = getFiniteTimeAtFrame(keyframeTimes, candidateFrame);
  const bestTimeDistance =
    bestTime === null ? Infinity : Math.abs(bestTime - targetTime);
  const candidateTimeDistance =
    candidateTime === null ? Infinity : Math.abs(candidateTime - targetTime);

  if (bestTimeDistance !== candidateTimeDistance) {
    return bestTimeDistance - candidateTimeDistance;
  }

  return bestFrame - candidateFrame;
}

function getFiniteTimeAtFrame(keyframeTimes: KeyframeTimes, frame: number) {
  const time = keyframeTimes[frame];

  return Number.isFinite(time) ? time : null;
}
